import { useEffect, useRef } from "react";
import { type EventStreamHandlers, useEventStream } from "@/components/EventStreamProvider";

/**
 * Register handlers for SSE events for the lifetime of the calling
 * component. Handlers are captured by ref each render so consumers can
 * pass fresh closures without forcing the subscription to churn.
 */
export function useEventSubscription(handlers: EventStreamHandlers): void {
  const { subscribe } = useEventStream();
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  useEffect(() => {
    const wrapper: EventStreamHandlers = {
      onMessageNew: (e) => handlersRef.current.onMessageNew?.(e),
      onMessageMedia: (e) => handlersRef.current.onMessageMedia?.(e),
      onConversationChanged: (id, patch) => handlersRef.current.onConversationChanged?.(id, patch),
      onConversationRead: (id) => handlersRef.current.onConversationRead?.(id),
      onChannelChanged: (id) => handlersRef.current.onChannelChanged?.(id),
      onChannelQuota: (e) => handlersRef.current.onChannelQuota?.(e),
      onOrderChanged: (id, status) => handlersRef.current.onOrderChanged?.(id, status),
      onReconnect: () => handlersRef.current.onReconnect?.(),
    };
    return subscribe(wrapper);
  }, [subscribe]);
}
